import { string as yupString } from 'yup';
import { AxiosError } from 'axios';

import { displayToast } from '../RTToast';
import { DocumentTypes } from '../../api/vouchers/helpers';
import { TravelFile, TravelFileLogType } from '../../api/travelFiles/oldModelTypes';
import { ContextTravelFile } from '../../api/travelFiles/context/context';
import { travelFileLogsTypesEnum } from '../../api/travelFiles/constants/TravelFileLogsTypes';
import travelFileApi from '../../api/travelFiles/api';

type getVoucherEmailSentLogTextProps = {
    voucherType: DocumentTypes;
    voucherNumber: number;
    emails: string[];
};
export const getVoucherEmailLogBody = (props: getVoucherEmailSentLogTextProps) => {
    const { voucherType, voucherNumber, emails } = props;
    return `${voucherType} ${voucherNumber} sent to: ${emails?.join(', ')}`;
};

/**
 * Record a sent email on the travel file.
 *
 * Called by the send points **after** `PdfViewerModal` reports a successful send. It used
 * to be a commented-out block inside `sendFilesToEmails`, reached through a callback the
 * modal never invoked — so every screen believed it was logging and none of them was.
 *
 * Silent when there is no travel file: the tour-level reports (Account Summary, Tour
 * Passengers) are emailed from a tour, not a file, and there is nothing to log against.
 * Failure to log must never surface as a failure to send, so errors are swallowed here —
 * `saveTravelFileLog` already toasts.
 */
export const logEmailSent = (props: {
    travelFilesContext: ContextTravelFile;
    user: string;
    body: string;
    travelFileId?: string;
}) => {
    const { travelFilesContext, user, body, travelFileId } = props;
    const tfId = travelFilesContext?.selected?._id || travelFileId;
    if (!tfId) return Promise.resolve();

    return saveTravelFileLog({
        travelFilesContext,
        travelFileId: tfId,
        newLog: {
            type: travelFileLogsTypesEnum.OTHER,
            body,
            user,
            createdAt: new Date()
        }
    }).catch(() => undefined);
};

export const sendFilesToEmails = async (props: {
    emailBody: string;
    emails: string[];
    pdfName?: string;
    travelFilesContext: ContextTravelFile;
    user: string;
    withToast?: boolean;
    comp?: any;
    location?: string;
    fileName?: string;
    attachments?: any[];
    title?: string;
}) => {
    const {
        // emailBody,
        emails,
        // travelFilesContext,
        // user,
        withToast,
        location,
        fileName,
        attachments,
        title
    } = props;

    if (emails?.length && emails.every((email) => yupString().email().isValidSync(email))) {
        // for single files
        const files = [];
        if (!attachments?.length) {
            files.push({
                fileName,
                location
            });
        }
        try {
            const response = await fetch('/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    emails,
                    location,
                    fileName,
                    attachments: attachments ?? files,
                    title
                })
            });
            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}`);
            }
            if (withToast) {
                displayToast('success', 'Emails sent!', `${emails?.join(', ')}`);
            }
        } catch (error) {
            console.error('Error sending email:', error);
            displayToast('error', 'Error!', 'Failed to send email');
        }

        // await saveTravelFileLog({
        //     travelFilesContext,
        //     newLog: {
        //         type: travelFileLogsTypesEnum.OTHER,
        //         body: emailBody,
        //         user,
        //         createdAt: new Date()
        //     }
        // });
    }
};

export const saveTravelFileLog = (props: {
    travelFilesContext: ContextTravelFile;
    newLog: TravelFileLogType;
    travelFileId?: string;
}) => {
    const { newLog, travelFilesContext, travelFileId } = props;
    const tfId = travelFilesContext.selected?._id || travelFileId;

    if (!tfId) {
        return Promise.reject(new Error('No travel file ID'));
    }

    return travelFileApi
        .addTravelFileLog({
            travelFileId: tfId,
            type: newLog.type,
            body: newLog.body,
            user: newLog.user as string
        })
        .then(() => {
            // Don't overwrite selected — it causes stale data (e.g., losing newly added services).
            // Logs are not displayed in the services section, so no need to update selected here.
        })
        .catch((err: AxiosError) => {
            displayToast('error', 'Error saving Log!', `The log: ${newLog.body}`);
            throw err;
        });
};
